import {
  type EngagementDetail,
  EngagementDetail as EngagementDetailSchema,
  type EngagementReferralFee,
  EngagementReferralFee as EngagementReferralFeeSchema,
} from '@uit/shared/schemas/engagement';
import {
  type VendorListResponse,
  VendorListResponse as VendorListResponseSchema,
} from '@uit/shared/schemas/vendor';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ReassignVendorAlert } from '../components/engagements/ReassignVendorAlert.js';
import { useApi } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { DOC_LABELS } from '../lib/labels.js';
import s from './EngagementDetailPage.module.scss';

function usd(n: number): string {
  return `$${n.toFixed(2)} / hr`;
}

export function EngagementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, error, loading, refetch } = useApi<EngagementDetail>(
    id ? `/api/engagements/${id}` : null,
    EngagementDetailSchema,
  );
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'admin';
  const isOwner = !!admin?.is_owner;
  // Owner-only referral fee (T-170) — fetched separately so it never reaches a
  // non-owner admin. Only relevant on a referred engagement.
  const { data: feeData, refetch: refetchFee } = useApi<EngagementReferralFee>(
    isOwner && id ? `/api/engagements/${id}/referral-fee` : null,
    EngagementReferralFeeSchema,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const [feeEdit, setFeeEdit] = useState<string | null>(null);
  const [feeSaving, setFeeSaving] = useState(false);
  const [feeMsg, setFeeMsg] = useState<string | null>(null);

  // ── Engagement edit (T-172) ────────────────────────────────────────
  // Inline edit of the two fields Eric asked for on the 08-22 call:
  // product_service, and the vendor. They save through DIFFERENT endpoints —
  // the vendor is not part of the engagement's own update contract — so a
  // vendor change also routes through the consequences confirm first.
  const [editing, setEditing] = useState(false);
  const [formService, setFormService] = useState('');
  const [formVendorId, setFormVendorId] = useState('');
  // Owner-only, and saved through its own endpoint (T-194) — the general PATCH
  // is admin-role gated and must never carry this field.
  const [formRate, setFormRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reassignedTo, setReassignedTo] = useState<string | null>(null);

  // Vendors for the picker. Only fetched in edit mode; a vendor that does not
  // exist yet is created on the Vendors page (T-199).
  const { data: vendorList } = useApi<VendorListResponse>(
    editing ? '/api/vendors?page_size=100' : null,
    VendorListResponseSchema,
  );

  // The form spelling of rate_to_client. Absent (non-owner) and null (owner,
  // never set) both render as an empty input; only an owner ever sees it.
  const rateString = data?.rate_to_client == null ? '' : String(data.rate_to_client);

  useEffect(() => {
    if (data && !editing) {
      setFormService(data.product_service);
      setFormVendorId(data.vendor_id);
      setFormRate(rateString);
    }
  }, [data, editing, rateString]);

  const vendorChanged = !!data && formVendorId !== '' && formVendorId !== data.vendor_id;
  const serviceChanged = !!data && formService.trim() !== data.product_service;
  const rateChanged = !!data && isOwner && formRate.trim() !== rateString;
  const newVendorName =
    vendorList?.items.find((v) => v.id === formVendorId)?.business_name ?? formVendorId;

  function startEdit(): void {
    if (!data) return;
    setFormService(data.product_service);
    setFormVendorId(data.vendor_id);
    setFormRate(rateString);
    setSaveMsg(null);
    setEditing(true);
  }

  function cancelEdit(): void {
    setEditing(false);
    setSaveMsg(null);
  }

  // Rename only — no vendor move, so no confirm. Straight through the
  // engagement's own PATCH.
  async function saveProductService(): Promise<boolean> {
    if (!id) return false;
    const res = await fetch(`/api/engagements/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ product_service: formService }),
    });
    if (!res.ok) {
      setSaveMsg(
        res.status === 400
          ? 'Enter a product/service name.'
          : res.status === 403
            ? 'You do not have permission to edit this engagement.'
            : `Could not save (${res.status}).`,
      );
      return false;
    }
    return true;
  }

  // Owner-only (T-194), through its own endpoint. Blank clears the rate.
  async function saveRateToClient(): Promise<boolean> {
    if (!id) return false;
    const trimmed = formRate.trim();
    const value = trimmed === '' ? null : Number(trimmed);
    if (value !== null && (!Number.isFinite(value) || value < 0)) {
      setSaveMsg('Enter a rate as a number, or leave it blank to clear it.');
      return false;
    }
    const res = await fetch(`/api/engagements/${id}/rate-to-client`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ rate_to_client: value }),
    });
    if (!res.ok && res.status !== 204) {
      setSaveMsg(
        res.status === 403
          ? 'Only an owner can set the rate to client.'
          : `Could not save the rate (${res.status}).`,
      );
      return false;
    }
    return true;
  }

  async function onSave(): Promise<void> {
    if (!data) return;
    setSaveMsg(null);
    if (vendorChanged) {
      // The vendor move needs its consequences acknowledged before anything is
      // written — including the rename, so one Save is one atomic-feeling step.
      setConfirmOpen(true);
      return;
    }
    if (!serviceChanged && !rateChanged) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      if (serviceChanged && !(await saveProductService())) return;
      if (rateChanged && !(await saveRateToClient())) return;
      setEditing(false);
      void refetch();
    } finally {
      setSaving(false);
    }
  }

  async function confirmReassign(): Promise<void> {
    if (!id || !data) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      if (serviceChanged && !(await saveProductService())) return;
      if (rateChanged && !(await saveRateToClient())) return;

      const res = await fetch(`/api/engagements/${id}/vendor`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ vendor_id: formVendorId }),
      });
      if (!res.ok) {
        setSaveMsg(
          res.status === 404
            ? 'That vendor no longer exists. Reload and try again.'
            : res.status === 403
              ? 'You do not have permission to reassign vendors.'
              : `Could not reassign (${res.status}).`,
        );
        return;
      }
      setReassignedTo(newVendorName);
      setConfirmOpen(false);
      setEditing(false);
      void refetch();
    } finally {
      setSaving(false);
    }
  }

  async function saveFee(raw: string): Promise<void> {
    if (!id) return;
    const trimmed = raw.trim();
    const value = trimmed === '' ? null : Number(trimmed);
    if (value !== null && !Number.isFinite(value)) {
      setFeeMsg('Enter a number.');
      return;
    }
    setFeeSaving(true);
    setFeeMsg(null);
    try {
      const res = await fetch(`/api/engagements/${id}/referral-fee`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ referral_fee_per_hour: value }),
      });
      if (!res.ok && res.status !== 204) throw new Error(String(res.status));
      setFeeEdit(null);
      await refetchFee();
    } catch {
      setFeeMsg('Could not save the referral fee. Please try again.');
    } finally {
      setFeeSaving(false);
    }
  }

  async function attachContract(file: File): Promise<void> {
    if (!id) return;
    setUploadBusy(true);
    setUploadMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/engagements/${id}/signed-contract`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        setUploadMsg(
          res.status === 415
            ? 'Only PDF files are allowed.'
            : res.status === 413
              ? 'File is too large (max 10 MB).'
              : `Upload failed (${res.status}).`,
        );
        return;
      }
      void refetch();
    } catch (err) {
      setUploadMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setUploadBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  if (loading && !data) return <div className={s.loading}>Loading…</div>;
  if (error || !data) {
    const is404 = error?.message.includes('404');
    return (
      <div className={s.notFound}>
        <Link to="/" className={s.back}>
          ← Back to resources
        </Link>
        <div className={s.notFoundBody}>
          {is404 ? 'Engagement not found.' : `Failed to load: ${error?.message ?? 'unknown'}`}
        </div>
      </div>
    );
  }

  const dates =
    data.start_date || data.end_date ? `${data.start_date ?? '—'} → ${data.end_date ?? '—'}` : null;

  return (
    <div className={s.page}>
      <Link to={`/resources/${data.resource_id}`} className={s.back}>
        ← Back to {data.resource_name}
      </Link>

      <div className={s.header}>
        <h1 className={s.title}>{data.product_service}</h1>
        <p className={s.subtitle}>
          Engagement · <span className={s.badge}>{data.status}</span> · the QuickBooks Service item
          name
        </p>
      </div>

      {reassignedTo && (
        <div className={s.banner} role="status">
          <div>
            <strong>Vendor changed to {reassignedTo}.</strong> Every engagement for{' '}
            {data.resource_name} now bills under it. Billing history is unchanged. A new signed
            agreement should be attached below.
          </div>
          <button
            type="button"
            className={s.bannerX}
            onClick={() => setReassignedTo(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <section className={s.section}>
        <h2 className={s.sectionTitle}>
          Engagement
          {isAdmin && (
            <span className={s.sectionActions}>
              {editing ? (
                <>
                  <button
                    type="button"
                    className={s.editBtn}
                    onClick={cancelEdit}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={s.saveBtn}
                    onClick={() => void onSave()}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                </>
              ) : (
                <button type="button" className={s.editBtn} onClick={startEdit}>
                  Edit
                </button>
              )}
            </span>
          )}
        </h2>

        {editing && (
          <div className={s.editGrid}>
            <div className={s.editField}>
              <label className={s.editLabel} htmlFor="eng-product-service">
                Product / service
              </label>
              <input
                id="eng-product-service"
                type="text"
                className={s.editInput}
                value={formService}
                onChange={(e) => setFormService(e.target.value)}
              />
              <div className={s.editHint}>
                The QuickBooks Service item name for this engagement.
              </div>
            </div>
            <div className={s.editField}>
              <label className={s.editLabel} htmlFor="eng-vendor">
                Vendor
              </label>
              <select
                id="eng-vendor"
                className={s.editSelect}
                value={formVendorId}
                onChange={(e) => setFormVendorId(e.target.value)}
              >
                {vendorList?.items.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.business_name}
                  </option>
                ))}
              </select>
              <div className={s.editHint}>
                {vendorChanged
                  ? 'Changing this moves the resource and every engagement it has — you will confirm first.'
                  : 'The business uIT pays. Missing one? Add it on the Vendors page.'}
              </div>
            </div>
            {isOwner && (
              <div className={s.editField}>
                <label className={s.editLabel} htmlFor="eng-rate-to-client">
                  Rate to client (owner-only)
                </label>
                <input
                  id="eng-rate-to-client"
                  type="text"
                  inputMode="decimal"
                  className={s.editInput}
                  placeholder="—"
                  value={formRate}
                  onChange={(e) => setFormRate(e.target.value)}
                />
                <div className={s.editHint}>
                  What uIT bills the client per hour. Only an owner can see or set it. Leave blank
                  to clear.
                </div>
              </div>
            )}
            {saveMsg && (
              <div className={s.msg} role="alert">
                {saveMsg}
              </div>
            )}
          </div>
        )}

        <dl className={s.grid}>
          <Field label="Client" value={data.client_name} />
          <Field label="Status" value={data.status} />
          <Field label="Dates" value={dates} />
          <Field label="uIT contact" value={data.uit_contact_email} />
          <Field
            label="Rate (to vendor)"
            value={data.rate_to_contractor == null ? null : usd(data.rate_to_contractor)}
          />
          {'rate_to_client' in data && (
            <Field
              label="Rate (to client)"
              value={data.rate_to_client == null ? null : usd(data.rate_to_client)}
            />
          )}
          {data.referred_by_vendor_id && (
            <Field label="Referred by" value={data.referred_by_vendor_name} />
          )}
        </dl>
      </section>

      {isOwner && data.referred_by_vendor_id && (
        <section className={s.section}>
          <h2 className={s.sectionTitle}>Referral fee (owner-only)</h2>
          <p className={s.muted} style={{ marginTop: 0 }}>
            What {data.referred_by_vendor_name} charges uIT per hour for this referral. Reference
            only — commissions are computed off-portal (T-197).
          </p>
          <div className={s.feeRow}>
            <input
              className={s.feeInput}
              inputMode="decimal"
              aria-label="Referral fee per hour"
              placeholder="—"
              value={
                feeEdit ??
                (feeData?.referral_fee_per_hour != null
                  ? String(feeData.referral_fee_per_hour)
                  : '')
              }
              onChange={(e) => setFeeEdit(e.target.value)}
            />
            <span className={s.muted}>/ hr</span>
            {feeEdit !== null &&
              feeEdit !==
                (feeData?.referral_fee_per_hour != null
                  ? String(feeData.referral_fee_per_hour)
                  : '') && (
                <button
                  type="button"
                  className={s.saveBtn}
                  disabled={feeSaving}
                  onClick={() => void saveFee(feeEdit ?? '')}
                >
                  {feeSaving ? 'Saving…' : 'Save'}
                </button>
              )}
            {feeMsg && (
              <span className={s.msg} role="alert">
                {feeMsg}
              </span>
            )}
          </div>
        </section>
      )}

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Belongs to</h2>
        <div className={s.rel}>
          <span className={s.relTag}>Resource</span>
          <div className={s.relMain}>
            <div className={s.relName}>{data.resource_name}</div>
            <div className={s.relSub}>the person doing the work</div>
          </div>
          <Link className={s.relGo} to={`/resources/${data.resource_id}`}>
            View resource →
          </Link>
        </div>
        <div className={s.rel}>
          <span className={s.relTag}>Vendor</span>
          <div className={s.relMain}>
            <div className={s.relName}>{data.business_name}</div>
            <div className={s.relSub}>the business uIT pays</div>
          </div>
          <Link className={s.relGo} to={`/vendors/${data.vendor_id}`}>
            View vendor →
          </Link>
        </div>
        <div className={s.rel}>
          <span className={s.relTag}>Client</span>
          <div className={s.relMain}>
            <div className={s.relName}>{data.client_name}</div>
            <div className={s.relSub}>who the work is billed to</div>
          </div>
        </div>
      </section>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Contract</h2>
        {data.signed_contract ? (
          <div className={s.docRow}>
            <span className={s.docKind}>
              {DOC_LABELS[data.signed_contract.kind] ?? data.signed_contract.kind}
            </span>
            <a
              className={s.docLink}
              href={data.signed_contract.download_url}
              target="_blank"
              rel="noreferrer"
            >
              {data.signed_contract.original_filename}
            </a>
          </div>
        ) : (
          <p className={s.muted}>No signed contract on file.</p>
        )}

        {reassignedTo && (
          <div className={s.needContract}>
            New agreement with <strong>{reassignedTo}</strong> — attach it here once signed. The
            engagement repoints to the new document and the old one is preserved.
          </div>
        )}

        {/* Admins can attach a contract signed outside BoldSign (T-146). */}
        {isAdmin && (
          <div className={s.uploadRow}>
            <input
              ref={inputRef}
              id="contract-upload"
              type="file"
              accept="application/pdf"
              className={s.hiddenInput}
              disabled={uploadBusy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void attachContract(f);
              }}
            />
            <label
              htmlFor="contract-upload"
              className={data.signed_contract ? s.replaceBtn : s.uploadBtn}
              aria-disabled={uploadBusy}
            >
              {uploadBusy
                ? 'Uploading…'
                : data.signed_contract
                  ? 'Replace contract (PDF)'
                  : '+ Attach signed contract (PDF)'}
            </label>
            {uploadMsg && (
              <span className={s.msg} role="alert">
                {uploadMsg}
              </span>
            )}
          </div>
        )}
      </section>

      {isAdmin && vendorChanged && (
        <ReassignVendorAlert
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          engagementId={data.id}
          resourceId={data.resource_id}
          resourceName={data.resource_name}
          oldVendorName={data.business_name}
          newVendorId={formVendorId}
          newVendorName={newVendorName}
          referredByVendorName={data.referred_by_vendor_name}
          busy={saving}
          error={saveMsg}
          onConfirm={() => void confirmReassign()}
        />
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value ?? <span className={s.muted}>—</span>}</dd>
    </div>
  );
}
